<script lang="ts">
	// Import styles
	import "../../init.css";
	// Import scripts and components
	import { page } from "$app/state";
	import { browser } from "$app/env";
	import { syncSessionValuesFromSearchParams } from "#lib/sessionValues.js";
	import { afterNavigate } from "$app/navigation";
	import Header from "#lib/Header.svelte";
	import Footer from "#lib/Footer.svelte";
	import Tracking from "#lib/Tracking.svelte";
	import AnnouncementBanner from "./AnnouncementBanner.svelte";
	import { setLangFromPath as setLang } from "#lib/langStore.js";
	import UXtweakScript from "#lib/UXtweakScript.svelte";
	import { setIsPreviewing, QueryLoader, VisualEditing } from "@sanity/sveltekit";
	import { client } from "#lib/sanity/client.js";
	import { PUBLIC_SANITY_DATASET } from "$app/env/public";
	import {
		Gift,
		HeartOutline,
		IconSprites,
		OutlinedCheckmark,
		Star,
		Social,
		Percent,
		SmartPhone,
		CheckOutline,
		ExclamationOutline,
		RightArrow,
		Heart,
		Grid,
		GroupIcon,
		Document,
		Price,
		LineChart,
		History,
		Home,
		Lightning,
		Calendar,
		CreditCard,
		setSanityDataset,
	} from "@ishavskraft/shared";

	setSanityDataset(PUBLIC_SANITY_DATASET);
	interface Props {
		data: {
			ENVIRONMENT: string;
			previewEnabled?: boolean;
		};
		children?: import("svelte").Snippet;
	}

	const topId = "start";
	let { data, children }: Props = $props();
	const isFront = $derived(page.url.pathname === "/");
	const isSpotprisen = $derived(page.url.pathname.split("/").includes("spotprisen"));
	const showTriangle = $derived(isFront || isSpotprisen);
	const previewEnabled = $derived(data?.previewEnabled ?? false);
	setIsPreviewing(previewEnabled);

	if (browser) {
		syncSessionValuesFromSearchParams(page.url.searchParams, { init: true });
	}

	// TODO: use page.data.languages to implement global language picker
	// $inspect("data.languages", page.data.languages);

	$effect(() => {
		const pathList = page.url.pathname.split("/");
		setLang(pathList[1]);
	});

	afterNavigate(({ shallow, type }) => {
		if (shallow && type === "goto") return;

		syncSessionValuesFromSearchParams(page.url.searchParams);
	});
</script>

<svelte:head>
	{#if !!data?.ENVIRONMENT?.match(/^preview?$/i)}
		<meta name="robots" content="noindex" />
	{:else}
		<script
			defer
			data-domain="ishavskraft.no"
			src="https://plausible.io/js/script.tagged-events.js"
		></script>
		<meta name="google-site-verification" content="7ZRJwzTy7xj32zhaw23ls9JeAs28tSiChT8ZCYIoFTM" />
	{/if}
</svelte:head>

<UXtweakScript />
<IconSprites
	icons={[
		Star,
		Gift,
		HeartOutline,
		OutlinedCheckmark,
		Social,
		SmartPhone,
		CheckOutline,
		Percent,
		ExclamationOutline,
		RightArrow,
		Heart,
		Grid,
		GroupIcon,
		Document,
		Price,
		LineChart,
		History,
		Home,
		Lightning,
		Calendar,
		CreditCard,
	]}
/>

<!-- #ids for "back to top" and "skip link" -->
<div class="site-container relative z-1" id={topId}>
	<Header class="mb-1" />

	<main id="hovedinnhold" class="pb-4">
		<AnnouncementBanner />

		<Tracking class="text-sm tablet:text-base leading-tight" banner={true}>
			<a href="/om/personvern/cookies" class="no-underline inline-grid items-end"
				><span
					>Dette nettstedet bruker følgende cookies:
					<strong>nødvendige</strong>
					for kjøp og rabatt; og valgfrie for
					<strong>funksjonelle</strong> tillegg,
					<strong>statistikk</strong>
					og
					<strong>markedsføring</strong>
					med tredjeparter.
					<span class="underline">Mer informasjon</span>
					→
				</span>
			</a>
		</Tracking>

		<QueryLoader enabled={previewEnabled} {client}>
			<!-- Re-key on path in preview so `useQuery` re-mounts when Studio editors navigate. -->
			{#if previewEnabled}
				{#key page.url.pathname}{@render children?.()}{/key}
			{:else}
				{@render children?.()}
			{/if}
		</QueryLoader>
	</main>

	<VisualEditing />

	<Footer backToTopHrefId={topId} />

	<div
		class:hidden={!showTriangle}
		class="ishavskraft-graphic-container absolute inset-0 z--1 overflow-hidden grid place-items-center"
		aria-hidden="true"
	>
		<div class="graphic absolute" aria-hidden="true"></div>
	</div>
</div>

<style lang="postcss">
	:global(html) {
		/* Offset scroll for key navigation focus */
		scroll-padding-block: 10rem;
	}

	.ishavskraft-graphic-container {
		.graphic {
			--_s: min(125vw, 1000px);
			--_t: calc(200px - 0.5 * var(--triangle-size, var(--_s)) - 10vw);
			background: var(--triangle) no-repeat center / contain;
			rotate: var(--triangle-rotate, 255deg);
			top: var(--triangle-top, var(--_t));
			width: var(--triangle-width, var(--triangle-size, var(--_s)));
			height: var(--triangle-height, var(--triangle-size, var(--_s)));
			/* flip */
			transform: scaleX(var(--triangle-scale-x, -1)) translateY(var(--triangle-translate-y, 0))
				translateX(var(--triangle-translate-x, 0));
		}
	}
</style>
